import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Stack,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useUploadFilesStep } from './useUploadFilesStep';
import { UploadFilesStepProps } from './types';
import FileUpload from '../../../FileUpload/FileUpload';
import DanelFileUploadItem from '../../../DanelFileUploadItem/DanelFileUploadItem';
import { InfoSvg } from '../../../Infra/Icons/InfoSvg';
import { VAULT_FILE_TYPES, EXCEL_FILE_TYPES } from './constants';
import styles from './UploadFilesStep.module.scss';
import { RowType } from '../../../../types/rowType';

export const UploadFilesStep: React.FC<UploadFilesStepProps> = () => {
  const {
    activeUploadTab,
    vaultFiles,
    returnExcel,
    rowsType,
    isDetecting,
    allVaultFiles,
    allExcelFiles,
    isRestoredSession,
    sessionName,
    sessionNameValidation,
    handleTabChange,
    handleVaultFilesSelect,
    handleVaultBankChange,
    handleRemoveVaultFile,
    handleExcelFilesSelect,
    handleRemoveExcelFile,
    handleReturnExcelChange,
    handlePositionsUploadChange,
    handleSessionNameChange,
  } = useUploadFilesStep();

  return (
    <Box className={styles.uploadContainer} data-testid="upload-files-step">
      {/* Flow Name Input */}
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#f5f5f5',
          padding: '16px',
          marginLeft: '16px',
          borderRadius: '8px',
          textAlign: 'left',
        }}
      >
        <TextField
          label="Flow Name"
          placeholder="Enter flow name"
          value={sessionName}
          onChange={e => handleSessionNameChange(e.target.value)}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          sx={{ width: 300 }}
          error={
            !!sessionNameValidation.error ||
            (sessionName.length > 0 && sessionName.trim().length < 3)
          }
          InputProps={{
            endAdornment: sessionNameValidation.isChecking ? (
              <InputAdornment position="end">
                <CircularProgress size={16} data-testid="flow-name-loading" />
              </InputAdornment>
            ) : null,
          }}
          data-testid="flow-name-input"
        />
      </Box>

      <Paper className={styles.uploadCard} elevation={0}>
        {/* Tabs */}
        <Tabs
          value={activeUploadTab}
          onChange={handleTabChange}
          className={styles.tabs}
          variant="fullWidth"
          data-testid="upload-tabs"
        >
          <Tab label="Vaults" value="vaults" data-testid="tab-vaults" />
          <Tab label="Excel" value="excel" data-testid="tab-excel" />
        </Tabs>

        {/* Vaults Tab Panel */}
        {activeUploadTab === 'vaults' && (
          <Box className={styles.tabPanel} data-testid="tab-panel-vaults">
            {/* Drag-and-drop area */}
            <FileUpload
              value={[]}
              disabled={isRestoredSession}
              onFilesChange={handleVaultFilesSelect}
              hideFileList={true}
              acceptedFormats={VAULT_FILE_TYPES}
              allowAllFilesInPicker={true}
              multiple={true}
              placeholder={{
                title: 'Upload Vault files',
                subtitle:
                  'Upload encrypted bank files for automated transaction processing',
                formats: 'Supported: All file types (.txt, .xml, .zip, etc.)',
              }}
              testId="file-upload-vault"
            />

            {/* File list - combined new and restored files */}
            {allVaultFiles.length > 0 && (
              <Box className={styles.fileList} data-testid="file-list-vaults">
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <Typography variant="h6">
                    Files ({allVaultFiles.length})
                  </Typography>
                  {isDetecting && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress
                        size={20}
                        data-testid="vault-detecting-spinner"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Detecting vault types...
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Stack spacing={2}>
                  {allVaultFiles.map((file, index) => (
                    <DanelFileUploadItem
                      key={file.id || `${file.name}-${index}`}
                      file={file}
                      index={index}
                      disabled={isRestoredSession}
                      onBankChange={
                        isRestoredSession
                          ? undefined
                          : bank => handleVaultBankChange(index, bank)
                      }
                      onRemove={
                        isRestoredSession
                          ? undefined
                          : () => handleRemoveVaultFile(index)
                      }
                      showBankFeatures={true}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Download Excel checkbox */}
            {vaultFiles.length > 0 && (
              <Box
                className={styles.checkboxContainer}
                data-testid="return-excel-container"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={returnExcel}
                      onChange={handleReturnExcelChange}
                      data-testid="return-excel-checkbox"
                    />
                  }
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography>
                        Download Excel file after processing
                      </Typography>
                      <Tooltip
                        title="Download processed data as Excel file for review"
                        placement="top"
                        arrow
                      >
                        <Box sx={{ display: 'flex', cursor: 'help' }}>
                          <InfoSvg
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  }
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            )}

            {/* Positions Upload checkbox */}
            {vaultFiles.length > 0 && (
              <Box
                className={styles.checkboxContainer}
                data-testid="positions-upload-container"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rowsType === RowType.ROW_TYPE_POSITION}
                      onChange={handlePositionsUploadChange}
                      data-testid="positions-upload-checkbox"
                    />
                  }
                  label={
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography>Positions Upload</Typography>
                      <Tooltip
                        title="Upload balance records (יתרה) instead of transaction records (תנועות)"
                        placement="top"
                        arrow
                      >
                        <Box sx={{ display: 'flex', cursor: 'help' }}>
                          <InfoSvg
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  }
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            )}

          </Box>
        )}

        {/* Excel Tab Panel */}
        {activeUploadTab === 'excel' && (
          <Box className={styles.tabPanel} data-testid="tab-panel-excel">
            <FileUpload
              value={[]}
              disabled={isRestoredSession}
              onFilesChange={handleExcelFilesSelect}
              hideFileList={true}
              acceptedFormats={EXCEL_FILE_TYPES}
              multiple={true}
              placeholder={{
                title: 'Upload Excel files',
                subtitle:
                  'Upload transaction files from custodian or portfolio systems',
                formats: 'Supported: .xlsx, .xls, .csv',
              }}
              testId="file-upload-excel"
            />

            {allExcelFiles.length > 0 && (
              <Box className={styles.fileList} data-testid="file-list-excel">
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Files ({allExcelFiles.length})
                </Typography>
                <Stack spacing={2}>
                  {allExcelFiles.map((file, index) => (
                    <DanelFileUploadItem
                      key={file.id || `${file.name}-${index}`}
                      file={file}
                      index={index}
                      disabled={isRestoredSession}
                      onRemove={
                        isRestoredSession
                          ? undefined
                          : () => handleRemoveExcelFile(index)
                      }
                      showBankFeatures={false}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};
